import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, forkJoin, from, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  CONFIG_VERSION,
  DEFAULT_INDICATORS,
  EMPTY_PROMPTS,
  DEFAULT_QUESTIONS,
} from '../shared/constants';
import { AuthService } from './auth/auth.service';
import { IndicatorsService } from './indicators/indicators';
import { PromptService } from './prompt/prompt';
import type { IConfig, IIndicators, IndicatorKind, IPrompts, IQuestion } from '../shared/models';

const STORAGE_KEY = 'cb_config';

// Config questionario + prompt AI. Sorgente condivisa = DB (ultima versione); localStorage è solo
// cache per domande e indicatori; i prompt restano vuoti fino alla risposta DB.
@Injectable({ providedIn: 'root' })
export class ConfigStore {
  // --- private ---
  private readonly _auth: AuthService = inject(AuthService);
  private readonly _prompts: PromptService = inject(PromptService);
  private readonly _indicators: IndicatorsService = inject(IndicatorsService);
  private readonly _cfgSignal: WritableSignal<IConfig> = signal<IConfig>(this._loadLocal());
  // --- public ---
  public readonly configSignal: Signal<IConfig> = this._cfgSignal.asReadonly();
  public readonly questionsSignal: Signal<IQuestion[]> = computed(() => this._cfgSignal().questions);
  public readonly indicatorsSignal: Signal<IIndicators> = computed(() => this._cfgSignal().indicators);
  // Versione attiva per strumento (id per fk_app_indicators_id, versione per "Versione N" in
  // Admin): usata da Survey per taggare una NUOVA compilazione con lo strumento in uso oggi.
  public readonly indicatorsVersionIdSignal: Signal<Record<IndicatorKind, string | null>> = computed(
    () => this._cfgSignal().indicatorsVersionId,
  );
  public readonly indicatorsVersioneSignal: Signal<Record<IndicatorKind, number | null>> = computed(
    () => this._cfgSignal().indicatorsVersione,
  );
  // Ultima versione dal DB in dev E prod (ogni ambiente ha il suo Supabase; editabile via
  // /admin, solo ADMIN). I prompt assenti restano vuoti, anche offline.
  public readonly promptsSignal: Signal<IPrompts> = computed(() => this._cfgSignal().prompts);
  // Id della riga app_prompt attiva (UUID v7): version stamping degli esiti Fase Zero
  // (Specifica_Sviluppatori_FaseZero §4). null se prompt assenti/offline — volutamente
  // fuori da IConfig/localStorage: uno stamp stantio è peggio di nessuno stamp.
  public readonly promptVersionIdSignal: WritableSignal<string | null> = signal<string | null>(null);

  // Carica l'ultima versione condivisa (prompt + domande) dal DB → seed della config. All'avvio (App).
  // Prompt assenti: stringhe vuote; indicatori assenti: valori locali/default.
  public loadRemote(): Observable<void> {
    return from(this._auth.whenReady()).pipe(
      switchMap(() => {
        const session = this._auth.sessionSignal();
        if (true === this._auth.enabled && null === session) {
          return of(undefined);
        }
        return this._loadRemote();
      }),
    );
  }

  private _loadRemote(): Observable<void> {
    // Ogni lettura fallita mantiene il fallback senza impedire le altre.
    return forkJoin({
      prompts: this._prompts.getLatestPrompts().pipe(catchError(() => of(null))),
      iip: this._indicators.getActiveVersion('iip').pipe(catchError(() => of(null))),
      ibap: this._indicators.getActiveVersion('ibap').pipe(catchError(() => of(null))),
    }).pipe(
      tap(({ prompts, iip, ibap }) => {
        this.promptVersionIdSignal.set(prompts?.id ?? null);
        this._cfgSignal.update((c) => ({
          ...c,
          // Prompt: il DB è l'UNICA sorgente; se non risponde nulla (tabella vuota o errore)
          // restano vuoti, ignorando la cache localStorage (potrebbe essere stantia).
          prompts: { ...EMPTY_PROMPTS, ...prompts?.prompts },
          // questions: solo DEFAULT_QUESTIONS (flusso documento e default pre-avvio); il set
          // dell'intervista arriva dal BE all'avvio (SessionStore._applyInterviewStart$).
          indicators: {
            iip: iip?.indicators ?? c.indicators.iip,
            ibap: ibap?.indicators ?? c.indicators.ibap,
          },
          indicatorsVersionId: {
            iip: iip?.id ?? c.indicatorsVersionId.iip,
            ibap: ibap?.id ?? c.indicatorsVersionId.ibap,
          },
          indicatorsVersione: {
            iip: iip?.versione ?? c.indicatorsVersione.iip,
            ibap: ibap?.versione ?? c.indicatorsVersione.ibap,
          },
        }));
        this._saveLocal(this._cfgSignal());
      }),
      map(() => undefined),
    );
  }

  public setConfig(cfg: IConfig): void {
    this._cfgSignal.set(cfg);
    this._saveLocal(cfg);
  }

  public defaults(): IConfig {
    return {
      version: CONFIG_VERSION,
      questions: structuredClone(DEFAULT_QUESTIONS),
      prompts: { ...EMPTY_PROMPTS },
      indicators: structuredClone(DEFAULT_INDICATORS),
      indicatorsVersionId: { iip: null, ibap: null },
      indicatorsVersione: { iip: null, ibap: null },
    };
  }

  private _loadLocal(): IConfig {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const cfg = JSON.parse(raw) as IConfig;
        if (cfg.version === CONFIG_VERSION) {
          return { ...cfg, prompts: { ...EMPTY_PROMPTS } };
        }
      }
    } catch {
      /* ignore parse errors */
    }
    return this.defaults();
  }

  private _saveLocal(cfg: IConfig): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...cfg, version: CONFIG_VERSION }));
  }
}
